import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Sliding submenu using CSS Grid for equal height panels
 * Renders via portal to #sliding-submenu-portal in main-app-content
 */
const SlidingSubmenu = ({
  isOpen = false,
  onClose,
  title,
  children,
  mainContent,
  showCloseButton = true,
  usePortal = false
}) => {
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    if (usePortal) {
      const target = document.getElementById('sliding-submenu-portal');
      setPortalTarget(target);
    }
  }, [usePortal]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const submenuContent = (
    <Box className="sliding-submenu" sx={{
      width: '100%',
      overflow: 'hidden',
    }}>
      {/* CSS Grid container - both panels have equal height */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '100% 100%', // Two full-width columns
        transform: isOpen ? 'translateX(-100%)' : 'translateX(0)',
        transition: 'transform 0.3s ease-in-out',
      }}>
        {/* Panel 1: Main content */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {mainContent}
        </Box>

        {/* Panel 2: Submenu content */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
        }}>
          {/* Mobile Layout */}
          <Box sx={{
            display: { xs: 'flex', md: 'none' },
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
          }}>
            {/* Title */}
            <Box sx={{
              '& .scale-info': { mb: 0, mt: 0 },
              '& .MuiTypography-root': {
                fontSize: '0.95rem',
                lineHeight: 1.2
              }
            }}>
              {title}
            </Box>
            {/* Buttons + Close */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {children}
              {showCloseButton && (
                <IconButton
                  onClick={handleClose}
                  size="small"
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Desktop Layout */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 2
          }}>
            <Box>{title}</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {children}
              {showCloseButton && (
                <IconButton
                  onClick={handleClose}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // If portal is enabled and target exists, render via portal; otherwise render in place
  if (usePortal && portalTarget) {
    return createPortal(submenuContent, portalTarget);
  }

  return submenuContent;
};

export default SlidingSubmenu;
